import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Button } from 'react-bootstrap';
import { BiDish } from 'react-icons/bi';
import { RiFilePaperFill } from 'react-icons/ri';
import { FaMoneyBill, FaChartLine, FaExclamationTriangle } from 'react-icons/fa';
import { getAllPendings } from '../../../../../../../../../../crud/management_api/local_sessions';
import './Pendings.scss';

type PendingKind = 'Pedido' | 'Recibo' | 'Caja' | 'Arqueo' | 'Efectivo';

interface PendingItem {
  id: number | string;
  kind: PendingKind;
  data: any;
}

interface PendingCategory {
  id: string;
  title: string;
  items: PendingItem[];
  priority: number;
  icon: React.ReactNode;
  description: string;
  color: string;
}

interface PendingsProps {
  localSessionId: number;
  onPendingsLoaded: (hasPendings: boolean) => void;
  excludeCajas?: boolean; // Optional prop to exclude "cajas" category (e.g., when closing session)
  context?: 'session' | 'shift'; // Context determines which pendings are blocking vs informational
  onTransferablePendingsFound?: (pendings: { pedidos: number; recibos: number }) => void; // Callback for transferable pendings count
  onArqueoAction?: () => void; // Callback for arqueo action button (only in shift context)
  onEfectivoAction?: () => void; // Callback for efectivo action button (to resolve pending cash)
}

export const Pendings: React.FC<PendingsProps> = ({ 
  localSessionId, 
  onPendingsLoaded, 
  excludeCajas = false,
  context = 'session',
  onTransferablePendingsFound,
  onArqueoAction,
  onEfectivoAction
}) => {
  const [categories, setCategories] = useState<PendingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // For shift context, only arqueo is a blocking pending
  // Pedidos and recibos can be transferred to the new shift
  const isShiftContext = context === 'shift';

  // Category configuration
  const getCategoryConfig = (): Omit<PendingCategory, 'items'>[] => [
    {
      id: 'pedidos',
      title: 'Pedidos pendientes',
      priority: 1,
      icon: <BiDish size={20} />,
      description: 'Pedidos que requieren atención',
      color: '#4F46E5'
    },
    {
      id: 'recibos',
      title: 'Recibos activos',
      priority: 2,
      icon: <RiFilePaperFill size={20} />,
      description: 'Recibos sin cerrar o procesar',
      color: '#059669'
    },
    {
      id: 'efectivo',
      title: 'Efectivo pendiente',
      priority: 3,
      icon: <FaMoneyBill size={20} />,
      description: 'Efectivo por aprobar',
      color: '#D97706'
    },
    {
      id: 'caja',
      title: 'Cajas abiertas',
      priority: 4,
      icon: <FaMoneyBill size={20} />,
      description: 'Cajas registradoras por cerrar',
      color: '#FC7556'
    },
    {
      id: 'arqueo',
      title: 'Arqueo pendiente',
      priority: 5,
      icon: <FaChartLine size={20} />,
      description: 'Cierre de turno pendiente',
      color: '#6B7280'
    }
  ];

  // Process API data into categories
  const processApiDataToCategories = useCallback((data: any): PendingCategory[] => {
    const categoryConfig = getCategoryConfig();
    const newCategories: PendingCategory[] = [];
    
    // Track transferable pendings for shift context
    let transferablePedidos = 0;
    let transferableRecibos = 0;

    categoryConfig.forEach(config => {
      // Skip "caja" category if excludeCajas is true
      if (excludeCajas && config.id === 'caja') {
        return;
      }
      
      // In shift context, skip pedidos and recibos from blocking categories
      // They will be transferred to the new shift
      if (isShiftContext && (config.id === 'pedidos' || config.id === 'recibos')) {
        // Count them for the callback but don't add as blocking
        if (config.id === 'pedidos' && data.pending_pedidos && Array.isArray(data.pending_pedidos)) {
          transferablePedidos = data.pending_pedidos.length;
        }
        if (config.id === 'recibos' && data.pending_receipts && Array.isArray(data.pending_receipts)) {
          transferableRecibos = data.pending_receipts.length;
        }
        return;
      }

      const items: PendingItem[] = [];

      switch (config.id) {
        case 'pedidos':
          if (data.pending_pedidos && Array.isArray(data.pending_pedidos)) {
            data.pending_pedidos.forEach((pedido: any) => {
              items.push({
                id: pedido.pedido_id,
                kind: 'Pedido',
                data: pedido
              });
            });
          }
          break;

        case 'recibos':
          if (data.pending_receipts && Array.isArray(data.pending_receipts)) {
            data.pending_receipts.forEach((receipt: any) => {
              items.push({
                id: receipt.receipt_id,
                kind: 'Recibo',
                data: receipt
              });
            });
          }
          break;

        case 'efectivo':
          // pending_cash can be an array or an object
          if (data.pending_cash) {
            if (Array.isArray(data.pending_cash) && data.pending_cash.length > 0) {
              data.pending_cash.forEach((cash: any, index: number) => {
                items.push({
                  id: `cash-${index}`,
                  kind: 'Efectivo',
                  data: cash
                });
              });
            } else if (typeof data.pending_cash === 'object' && !Array.isArray(data.pending_cash) && Object.keys(data.pending_cash).length > 0) {
              // Handle object format
              items.push({
                id: 'CASH',
                kind: 'Efectivo',
                data: data.pending_cash
              });
            }
          }
          break;

        case 'caja':
          if (data.pending_cajas && Array.isArray(data.pending_cajas)) {
            data.pending_cajas.forEach((caja: any) => {
              items.push({
                id: caja.workstation_id,
                kind: 'Caja',
                data: caja
              });
            });
          }
          break;

        case 'arqueo':
          // pending_arqueo is a boolean, not an array
          if (data.pending_arqueo === true) {
            items.push({
              id: 'arqueo-pending',
              kind: 'Arqueo',
              data: { requires_arqueo: true }
            });
          }
          break;
      }

      // Only add category if it has items
      if (items.length > 0) {
        newCategories.push({
          ...config,
          items
        });
      }
    });
    
    // Notify about transferable pendings in shift context
    if (isShiftContext && onTransferablePendingsFound) {
      onTransferablePendingsFound({ pedidos: transferablePedidos, recibos: transferableRecibos });
    }

    return newCategories.sort((a, b) => a.priority - b.priority);
  }, [excludeCajas, isShiftContext, onTransferablePendingsFound]);

  useEffect(() => {
    if (!localSessionId) {
      setCategories([]);
      setLoading(false);
      onPendingsLoaded(false);
      return;
    }

    setLoading(true);
    getAllPendings(localSessionId)
      .then(({ data }) => {
        const newCategories = processApiDataToCategories(data);
        setCategories(newCategories);
        setError(null);
        onPendingsLoaded(newCategories.length > 0);
      })
      .catch((error) => {
        console.error('Error loading pendings:', error);
        setError('Error cargando pendientes');
        onPendingsLoaded(true); // Assume there are pendings if error
      })
      .finally(() => setLoading(false));
  }, [localSessionId]);

  // Get item display details
  const getItemDetails = (item: PendingItem) => {
    switch (item.kind) {
      case 'Pedido':
        return {
          title: `Pedido #${item.data.pedido_id}`,
          subtitle: item.data.pedido_type_name || 'Pedido',
          meta: item.data.mesa_num ? `Mesa ${item.data.mesa_num}` : 
                item.data.user_name ? `Mesero: ${item.data.user_name}` : 
                item.data.client_name ? `Cliente: ${item.data.client_name}` : ''
        };

      case 'Recibo':
        return {
          title: `Recibo #${item.data.receipt_number || item.data.receipt_id}`,
          subtitle: item.data.pedido_type_name || 'Recibo',
          meta: item.data.total_due ? `$${parseInt(item.data.total_due).toLocaleString('es-CL')}` : ''
        };

      case 'Efectivo':
        return {
          title: 'Efectivo pendiente',
          subtitle: 'Efectivo por aprobar',
          meta: item.data.amount ? `$${parseInt(item.data.amount).toLocaleString('es-CL')}` : ''
        };

      case 'Caja':
        return {
          title: `${item.data.workstation_name || `Caja #${item.data.workstation_id}`}`,
          subtitle: 'Caja registradora abierta',
          meta: item.data.current_amount ? `$${parseInt(item.data.current_amount).toLocaleString('es-CL')}` : ''
        };

      case 'Arqueo':
        return {
          title: 'Arqueo pendiente',
          subtitle: 'Cierre de turno',
          meta: 'Requiere atención'
        };

      default:
        return {
          title: item.kind,
          subtitle: '',
          meta: ''
        };
    }
  };

  if (loading) {
    return (
      <div className="pendings-loading">
        <Spinner animation="border" variant="warning" />
        <p>Verificando pendientes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pendings-error">
        <FaExclamationTriangle size={24} />
        <p>{error}</p>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="pendings-empty">
        <div className="pendings-empty-icon">✓</div>
        <h4>Sin pendientes bloqueantes</h4>
        <p>
          {isShiftContext 
            ? 'No hay elementos que bloqueen la creación del nuevo turno.'
            : 'No hay elementos pendientes. Puedes proceder a finalizar la sesión.'}
        </p>
      </div>
    );
  }

  return (
    <div className="pendings-container">
      <div className="pendings-header">
        <FaExclamationTriangle className="pendings-warning-icon" />
        <div className="pendings-header-text">
          <h4>Elementos pendientes encontrados</h4>
          <p>
            {isShiftContext
              ? 'Los siguientes elementos deben resolverse antes de crear un nuevo turno:'
              : 'Debes resolver los siguientes elementos antes de finalizar la sesión:'}
          </p>
        </div>
      </div>

      <div className="pendings-categories">
        {categories.map((category) => (
          <div key={category.id} className="pending-category">
            <div 
              className="pending-category-header"
              style={{ borderLeftColor: category.color }}
            >
              <div className="pending-category-icon" style={{ color: category.color }}>
                {category.icon}
              </div>
              <div className="pending-category-info">
                <h5 className="pending-category-title">
                  {category.title}
                  <span className="pending-category-count">
                    {category.items.length}
                  </span>
                </h5>
                <p className="pending-category-description">
                  {category.description}
                </p>
              </div>
            </div>

            <div className="pending-category-items">
              {category.items.map((item, index) => {
                const details = getItemDetails(item);
                return (
                  <div key={`${item.kind}-${item.id}-${index}`} className="pending-item">
                    <div className="pending-item-info">
                      <span className="pending-item-title">{details.title}</span>
                      <span className="pending-item-subtitle">{details.subtitle}</span>
                      {details.meta && (
                        <span className="pending-item-meta">{details.meta}</span>
                      )}
                    </div>
                    {/* Show action button for arqueo in shift context */}
                    {item.kind === 'Arqueo' && isShiftContext && onArqueoAction && (
                      <div className="pending-item-action">
                        <Button
                          variant="none"
                          className="arqueo-action-button"
                          onClick={onArqueoAction}
                        >
                          Crear Arqueo
                        </Button>
                      </div>
                    )}
                    {/* Show action button for efectivo */}
                    {item.kind === 'Efectivo' && onEfectivoAction && (
                      <div className="pending-item-action">
                        <Button
                          variant="none"
                          className="efectivo-action-button"
                          onClick={onEfectivoAction}
                        >
                          Ver Efectivo
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="pendings-footer">
        <p className="pendings-footer-text">
          <strong>Importante:</strong> {isShiftContext
            ? 'Resuelve los elementos pendientes antes de continuar. El arqueo y el efectivo pendiente deben resolverse antes de crear un nuevo turno.'
            : 'Resuelve todos los elementos pendientes antes de continuar. Una vez resueltos, podrás finalizar la sesión normalmente.'}
        </p>
      </div>
    </div>
  );
};

export default Pendings;
